/**
 * Harness Playground engine — a REAL (minimal) agent harness in the browser.
 *
 * This is not a simulation: it speaks the exact OpenAI Chat Completions wire
 * protocol that OpenClaw / Cline / Aider / Continue speak, against the same
 * `https://api.aridel.ai/v1` endpoint, with the signed-in user's own
 * `aridel_live_*` key (via aridel-client). The only difference from a "big"
 * harness is that the tools operate on a small in-memory virtual workspace
 * instead of a real repo.
 *
 * Two demos:
 *   runAgentLoop  — the sequential tool loop (request → tool_calls → execute
 *                   locally → send results back → … → finish_reason "stop").
 *   runFanOut     — N parallel, independent requests (one per file), each
 *                   routed on its own merits, then one combine request.
 */

import { chat as aridelChat } from './aridel-client';
import type { ChatCompletionChunk } from './aridel-client';

// ---------------------------------------------------------------------------
// Virtual workspace
// ---------------------------------------------------------------------------

export const SEED_FILES: Record<string, string> = {
  'config.json': `{
  "service": "billing-worker",
  "port": 3000,
  "retries": 2,
  "log_level": "warn"
}
`,
  'server.js': `const http = require('http');
const { port } = require('./config.json');

// Naive email check — duplicated in three services, known to reject
// plus-addressing. Candidate for the shared validator.
function isValidEmail(s) {
  return /^[^@]+@[^@]+\\.[^@]+$/.test(s);
}

http.createServer((req, res) => {
  res.end('ok');
}).listen(port);
`,
  'sales_q3.csv': `month,region,units,revenue_usd
2026-04,EU,1210,60500
2026-04,US,980,49000
2026-05,EU,1340,67000
2026-05,US,1105,55250
2026-06,EU,1615,80750
2026-06,US,1490,74500
`,
  'product_notes.md': `# Q3 retro notes

The billing worker missed its latency target twice in June — both incidents
traced to cold starts after deploys. Marketing wants the September launch
announcement to lead with the routing-savings dashboard. Support tickets
about the CSV export doubled after the pricing change; most were confusion
about the new per-seat column, not bugs.
`,
};

export class VirtualWorkspace {
  files: Record<string, string>;
  /** Paths written during the current run (for UI highlighting). */
  written: Set<string> = new Set();

  constructor(seed: Record<string, string> = SEED_FILES) {
    this.files = { ...seed };
  }

  list(): string {
    return Object.keys(this.files).sort().join('\n');
  }

  read(path: string): string {
    if (!(path in this.files)) {
      return `ERROR: no such file: ${path}. Files: ${Object.keys(this.files).join(', ')}`;
    }
    return this.files[path];
  }

  write(path: string, content: string): string {
    this.files[path] = content;
    this.written.add(path);
    return `Wrote ${content.length} chars to ${path}`;
  }
}

// OpenAI tool definitions — the shape every harness sends.
export const WORKSPACE_TOOLS = [
  {
    type: 'function',
    function: {
      name: 'list_files',
      description: 'List the files in the workspace.',
      parameters: { type: 'object', properties: {} },
    },
  },
  {
    type: 'function',
    function: {
      name: 'read_file',
      description: 'Read a file from the workspace.',
      parameters: {
        type: 'object',
        properties: { path: { type: 'string', description: 'File path to read' } },
        required: ['path'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'write_file',
      description: 'Create or overwrite a file in the workspace.',
      parameters: {
        type: 'object',
        properties: {
          path: { type: 'string' },
          content: { type: 'string' },
        },
        required: ['path', 'content'],
      },
    },
  },
];

function executeTool(ws: VirtualWorkspace, name: string, argsJson: string): string {
  let args: Record<string, unknown> = {};
  try {
    args = argsJson ? JSON.parse(argsJson) : {};
  } catch {
    return `ERROR: malformed tool arguments: ${argsJson}`;
  }
  switch (name) {
    case 'list_files':
      return ws.list();
    case 'read_file':
      return ws.read(String(args.path ?? ''));
    case 'write_file':
      return ws.write(String(args.path ?? ''), String(args.content ?? ''));
    default:
      return `ERROR: unknown tool ${name}`;
  }
}

// ---------------------------------------------------------------------------
// Streaming accumulation (mirrors the server's tool-call delta assembly)
// ---------------------------------------------------------------------------

interface ToolCall {
  id?: string;
  type: string;
  function: { name?: string; arguments: string };
}

function accumulateToolCallDelta(assembly: Map<number, ToolCall>, delta: any): void {
  const idx = typeof delta.index === 'number' ? delta.index : 0;
  const existing = assembly.get(idx) ?? { type: 'function', function: { arguments: '' } };
  if (delta.id) existing.id = delta.id;
  if (delta.type) existing.type = delta.type;
  const fn = delta.function ?? {};
  if (fn.name) existing.function.name = fn.name;
  if (fn.arguments) existing.function.arguments += fn.arguments;
  assembly.set(idx, existing);
}

// ---------------------------------------------------------------------------
// Event model for the UI
// ---------------------------------------------------------------------------

export interface TurnRecord {
  /** 1-based HTTP request number within the run. */
  requestNo: number;
  /** The exact request body sent over the wire (auth travels in the header). */
  requestBody: Record<string, unknown>;
  model?: string;          // slug echoed by the API
  routing?: Record<string, unknown>;
  text: string;
  toolCalls: ToolCall[];
  toolResults: Array<{ name: string; args: string; result: string }>;
  usage?: Record<string, unknown>;
  finishReason?: string;
  error?: string;
}

export interface LoopCallbacks {
  onTurnStart: (turn: TurnRecord) => void;
  /** Fired on every mutation of the current turn (stream delta, tool result…). */
  onTurnUpdate: (turn: TurnRecord) => void;
  onTurnEnd: (turn: TurnRecord) => void;
}

export interface RunOptions {
  /** OSS-only accounts must constrain routing like the chat view does. */
  modelPool?: string[];
  signal?: AbortSignal;
  maxTurns?: number;
}

// ---------------------------------------------------------------------------
// Demo 1 — the sequential agent tool loop
// ---------------------------------------------------------------------------

export async function runAgentLoop(
  task: string,
  ws: VirtualWorkspace,
  cb: LoopCallbacks,
  opts: RunOptions = {},
): Promise<TurnRecord[]> {
  const maxTurns = opts.maxTurns ?? 6;
  const messages: Array<Record<string, unknown>> = [
    { role: 'user', content: task },
  ];
  const turns: TurnRecord[] = [];

  for (let i = 0; i < maxTurns; i++) {
    const requestBody: Record<string, unknown> = {
      model: 'auto',
      messages: [...messages],
      tools: WORKSPACE_TOOLS,
      max_tokens: 1024,
      ...(opts.modelPool ? { model_pool: opts.modelPool } : {}),
    };
    const turn: TurnRecord = {
      requestNo: i + 1,
      requestBody,
      text: '',
      toolCalls: [],
      toolResults: [],
    };
    turns.push(turn);
    cb.onTurnStart(turn);

    const assembly = new Map<number, ToolCall>();
    try {
      const stream = aridelChat.completions.stream(
        requestBody as any,
        { signal: opts.signal },
      );
      for await (const chunk of stream as AsyncIterable<ChatCompletionChunk>) {
        if (chunk.model) turn.model = chunk.model;
        if ((chunk as any).routing) turn.routing = (chunk as any).routing;
        if (chunk.usage) turn.usage = chunk.usage as any;
        const choice = chunk.choices?.[0];
        if (choice?.finish_reason) turn.finishReason = choice.finish_reason;
        const delta: any = choice?.delta ?? {};
        if (delta.content) turn.text += delta.content;
        for (const tcDelta of delta.tool_calls ?? []) {
          accumulateToolCallDelta(assembly, tcDelta);
        }
        cb.onTurnUpdate(turn);
      }
    } catch (err: any) {
      if (opts.signal?.aborted) throw err;
      turn.error = err?.message ?? String(err);
      cb.onTurnEnd(turn);
      return turns;
    }

    turn.toolCalls = [...assembly.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, tc]) => tc);

    if (turn.toolCalls.length === 0) {
      // The model finished — the loop is done.
      cb.onTurnEnd(turn);
      return turns;
    }

    // Execute each tool LOCALLY (this is what a harness does on the user's
    // machine) and append the results for the next request.
    messages.push({
      role: 'assistant',
      content: turn.text || null,
      tool_calls: turn.toolCalls,
    });
    for (const tc of turn.toolCalls) {
      const name = tc.function.name ?? 'unknown';
      const result = executeTool(ws, name, tc.function.arguments);
      turn.toolResults.push({ name, args: tc.function.arguments, result });
      messages.push({ role: 'tool', tool_call_id: tc.id, content: result });
      cb.onTurnUpdate(turn);
    }
    cb.onTurnEnd(turn);
  }
  return turns;
}

// ---------------------------------------------------------------------------
// Demo 2 — parallel fan-out (N independent requests + one combine request)
// ---------------------------------------------------------------------------

export interface FanOutCard {
  label: string;          // e.g. the file name, or "combine"
  requestBody: Record<string, unknown>;
  model?: string;
  routing?: Record<string, unknown>;
  text: string;
  usage?: Record<string, unknown>;
  error?: string;
  done: boolean;
}

export async function runFanOut(
  ws: VirtualWorkspace,
  cb: { onUpdate: (cards: FanOutCard[]) => void },
  opts: RunOptions = {},
): Promise<FanOutCard[]> {
  const paths = Object.keys(ws.files).sort();
  const cards: FanOutCard[] = paths.map((p) => ({
    label: p,
    requestBody: {
      model: 'auto',
      max_tokens: 300,
      messages: [{
        role: 'user',
        content: `Summarize this file (${p}) in 2 sentences:\n\n${ws.files[p]}`,
      }],
      ...(opts.modelPool ? { model_pool: opts.modelPool } : {}),
    },
    text: '',
    done: false,
  }));
  cb.onUpdate([...cards]);

  const runOne = async (card: FanOutCard) => {
    try {
      const stream = aridelChat.completions.stream(card.requestBody as any, { signal: opts.signal });
      for await (const chunk of stream as AsyncIterable<ChatCompletionChunk>) {
        if (chunk.model) card.model = chunk.model;
        if ((chunk as any).routing) card.routing = (chunk as any).routing;
        if (chunk.usage) card.usage = chunk.usage as any;
        const delta = chunk.choices?.[0]?.delta;
        if (delta?.content) card.text += delta.content;
        cb.onUpdate([...cards]);
      }
    } catch (err: any) {
      if (!opts.signal?.aborted) card.error = err?.message ?? String(err);
    } finally {
      card.done = true;
      cb.onUpdate([...cards]);
    }
  };

  // THE fan-out: all requests in flight at once — to Aridel these are just
  // N independent requests arriving together, each routed on its own content.
  await Promise.all(cards.map(runOne));
  if (opts.signal?.aborted) return cards;

  const combine: FanOutCard = {
    label: 'combine',
    requestBody: {
      model: 'auto',
      max_tokens: 400,
      messages: [{
        role: 'user',
        content:
          'Combine these file summaries into one short workspace overview ' +
          '(3-4 sentences):\n\n' +
          cards.map((c) => `## ${c.label}\n${c.text}`).join('\n\n'),
      }],
      ...(opts.modelPool ? { model_pool: opts.modelPool } : {}),
    },
    text: '',
    done: false,
  };
  cards.push(combine);
  cb.onUpdate([...cards]);
  await runOne(combine);
  return cards;
}

// ---------------------------------------------------------------------------
// The real-harness config this playground mirrors (for the copy-paste block)
// ---------------------------------------------------------------------------

export const OPENCLAW_CONFIG_SNIPPET = `{
  "models": {
    "mode": "merge",
    "providers": {
      "aridel": {
        "baseUrl": "https://api.aridel.ai/v1",
        "apiKey": "aridel_live_...",
        "api": "openai-completions",
        "models": [
          { "id": "auto", "name": "Aridel Auto-Route",
            "contextWindow": 128000, "maxTokens": 8192 }
        ]
      }
    }
  },
  "agents": { "defaults": { "model": { "primary": "aridel/auto" } } }
}`;
